import { useMemo, useState } from "react";
import { Link } from "react-router";
import { describeError, isApiError } from "@/api/client";
import {
  Badge,
  Button,
  Combobox,
  InlineAlert,
  RadioGroup,
  RelativeTime,
  Select,
  TextArea,
  TextField,
  useToast,
  type SelectOption,
} from "@/components";
import { useOdorTree } from "@/features/knowledge/api";
import {
  kindLabel,
  searchMaterials,
  searchSubstances,
  type MaterialListItem,
  type SubstanceListItem,
} from "@/features/materials/api";
import { useDecideReviewItem, type DecisionParams, type ReviewDecision, type ReviewItemDetail } from "./api";
import { EvidenceList } from "./EvidenceList";
import styles from "./ReviewPage.module.css";

const CAS_PATTERN = /^\d{2,7}-\d{2}-\d$/;

const ACCEPT_LABELS: Record<string, string> = {
  link: "Link the name",
  confirm_substance: "Confirm the match",
  choose_cas: "Keep this CAS number",
  set_cas: "Record the CAS number",
  set_odor_path: "Record the odor path",
};

const DECISION_TOASTS: Record<ReviewDecision, string> = {
  accepted: "Accepted",
  rejected: "Rejected",
  deferred: "Deferred",
};

const STATUS_LOOK = {
  open: { label: "Open", tone: "warning" },
  deferred: { label: "Deferred", tone: "neutral" },
  accepted: { label: "Accepted", tone: "success" },
  rejected: { label: "Rejected", tone: "neutral" },
} as const;

function substanceLabel(substance: SubstanceListItem): string {
  return substance.cas ? `${substance.preferred_name} (${substance.cas})` : substance.preferred_name;
}

function SubstanceOption({ substance }: { substance: SubstanceListItem }) {
  return (
    <span className={styles.option}>
      <span>{substance.preferred_name}</span>
      <span className={styles.subtle}>
        {[substance.cas, substance.kind === "ncs" ? "natural" : substance.kind, `${substance.material_count} materials`]
          .filter(Boolean)
          .join(", ")}
      </span>
    </span>
  );
}

function MaterialOption({ material }: { material: MaterialListItem }) {
  return (
    <span className={styles.option}>
      <span>{material.name}</span>
      <span className={styles.subtle}>{[material.cas, kindLabel(material.kind)].filter(Boolean).join(", ")}</span>
    </span>
  );
}

function OdorPathPicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const tree = useOdorTree();
  const families = useMemo<SelectOption[]>(
    () => (tree.data?.families ?? []).map((family) => ({ value: family.id, label: family.name_en })),
    [tree.data],
  );
  const descriptors = useMemo<SelectOption[]>(() => {
    const all = [...(tree.data?.families ?? []).flatMap((family) => family.children), ...(tree.data?.unplaced ?? [])];
    return [
      { value: "none", label: "None" },
      ...all
        .sort((a, b) => a.name_en.localeCompare(b.name_en))
        .map((node) => ({ value: node.id, label: node.name_en })),
    ];
  }, [tree.data]);
  const slots = [value[0] ?? null, value[1] ?? null, value[2] ?? null, value[3] ?? null];

  function setSlot(index: number, id: string | null) {
    const next = [...slots];
    next[index] = id === "none" ? null : id;
    onChange(next.filter((item): item is string => Boolean(item)));
  }

  return (
    <div className={styles.pathPicker}>
      <Select label="Family" options={families} value={slots[0]} onValueChange={(id) => setSlot(0, id)} />
      {[1, 2, 3].map((index) => (
        <Select
          key={index}
          label={`Descriptor ${index}`}
          optional
          options={descriptors}
          value={slots[index] ?? "none"}
          onValueChange={(id) => setSlot(index, id)}
          disabled={!slots[index - 1]}
        />
      ))}
    </div>
  );
}

/** Evidence, the entity in question and the decision controls for one review item. */
export function DecisionPanel({
  item,
  canEdit,
  onDecided,
}: {
  item: ReviewItemDetail;
  canEdit: boolean;
  onDecided: () => void;
}) {
  const decide = useDecideReviewItem();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [linkMode, setLinkMode] = useState<"substance" | "material">("substance");
  const [suggestion, setSuggestion] = useState<string | null>(item.suggestions[0]?.id ?? null);
  const [substance, setSubstance] = useState<SubstanceListItem | null>(null);
  const [material, setMaterial] = useState<MaterialListItem | null>(null);
  const [casChoice, setCasChoice] = useState<string | null>(
    item.cas_options.length === 1 ? item.cas_options[0]! : null,
  );
  const [otherCas, setOtherCas] = useState("");
  const [termIds, setTermIds] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const decidable = canEdit && (item.status === "open" || item.status === "deferred");
  const action = item.accept_action;
  const entity = item.entity;
  const look = STATUS_LOOK[item.status];

  function acceptParams(): DecisionParams | string {
    if (action === "link") {
      if (linkMode === "material")
        return material ? { material_id: material.id } : "Choose the material this name refers to";
      const id = substance?.id ?? suggestion;
      return id ? { substance_id: id } : "Choose the substance this name refers to";
    }
    if (action === "confirm_substance") return substance ? { substance_id: substance.id } : {};
    if (action === "choose_cas") {
      const cas = casChoice === "other" ? otherCas.trim() : casChoice;
      if (!cas) return "Choose the CAS number to keep";
      return CAS_PATTERN.test(cas) ? { cas } : "CAS numbers look like 106-22-9";
    }
    if (action === "set_cas" && item.reason_code === "cas_checksum") {
      const cas = otherCas.trim();
      if (!cas) return "Enter the corrected CAS number";
      return CAS_PATTERN.test(cas) ? { cas } : "CAS numbers look like 106-22-9";
    }
    if (action === "set_odor_path") return termIds.length ? { term_ids: termIds } : "Choose at least the family";
    return {};
  }

  function submit(decision: ReviewDecision) {
    const params = decision === "accepted" ? acceptParams() : {};
    if (typeof params === "string") {
      setProblem(params);
      return;
    }
    setProblem(null);
    decide.mutate(
      { id: item.id, decision, note: note.trim(), params },
      {
        onSuccess: () => {
          toast.success(`${DECISION_TOASTS[decision]}: ${item.entity_label}`);
          onDecided();
        },
        onError: (error) => {
          if (isApiError(error, 400)) setProblem(error.detail);
          else toast.error("Couldn’t record the decision", { description: describeError(error) });
        },
      },
    );
  }

  return (
    <article className={styles.panel} aria-labelledby="review-item-title">
      <header className={styles.panelHeader}>
        <div className={styles.panelMeta}>
          <Badge tone={look.tone}>{look.label}</Badge>
          <span className={styles.subtle}>{item.reason_label}</span>
          <span className={styles.subtle}>
            Added <RelativeTime value={item.created_at} />
          </span>
        </div>
        <h2 id="review-item-title" className={styles.panelTitle}>
          {item.material_id ? (
            <Link to={`/materials/${item.material_id}`}>{item.entity_label}</Link>
          ) : (
            item.entity_label
          )}
        </h2>
        <p className={styles.summary}>{item.summary}</p>
        {item.description ? <p className={styles.muted}>{item.description}</p> : null}
      </header>

      <section aria-labelledby="evidence-title" className={styles.block}>
        <h3 id="evidence-title" className={styles.blockTitle}>
          Evidence
        </h3>
        <EvidenceList evidence={item.evidence} />
      </section>

      {entity.kind ? (
        <section aria-labelledby="entity-title" className={styles.block}>
          <h3 id="entity-title" className={styles.blockTitle}>
            {entity.kind === "material"
              ? "The material now"
              : entity.kind === "supplier_product"
                ? "The supplier listing now"
                : entity.kind === "source_record"
                  ? "The source record"
                  : "The substance now"}
          </h3>
          <dl className={styles.evidence}>
            {entity.kind === "material" ? (
              <>
                <div className={styles.evidenceRow}>
                  <dt>Kind</dt>
                  <dd>{kindLabel(entity.material_kind ?? "other")}</dd>
                </div>
                <div className={styles.evidenceRow}>
                  <dt>Substance</dt>
                  <dd>
                    {entity.substance ? (
                      substanceLabel(entity.substance)
                    ) : (
                      <span className={styles.subtle}>Not linked</span>
                    )}
                  </dd>
                </div>
              </>
            ) : null}
            {entity.kind === "supplier_product" ? (
              <>
                <div className={styles.evidenceRow}>
                  <dt>Listing</dt>
                  <dd>
                    {entity.url ? (
                      <a href={entity.url} target="_blank" rel="noreferrer">
                        {entity.title}
                      </a>
                    ) : (
                      entity.title
                    )}{" "}
                    <span className={styles.subtle}>{entity.supplier}</span>
                  </dd>
                </div>
                <div className={styles.evidenceRow}>
                  <dt>CAS on the listing</dt>
                  <dd className={styles.mono}>{entity.cas ?? "—"}</dd>
                </div>
              </>
            ) : null}
            {entity.kind === "source_record" ? (
              <div className={styles.evidenceRow}>
                <dt>Linked substance</dt>
                <dd>
                  {entity.substance ? substanceLabel(entity.substance) : <span className={styles.subtle}>None</span>}
                </dd>
              </div>
            ) : null}
            {entity.kind === "substance" ? (
              <div className={styles.evidenceRow}>
                <dt>CAS numbers now</dt>
                <dd className={styles.mono}>{entity.cas_numbers?.join(", ") || "None"}</dd>
              </div>
            ) : null}
          </dl>
        </section>
      ) : null}

      {item.status === "accepted" || item.status === "rejected" ? (
        <section className={styles.block} aria-label="Decision">
          <h3 className={styles.blockTitle}>Decision</h3>
          <p>
            {look.label}
            {item.decided_by ? ` by ${item.decided_by}` : ""} <RelativeTime value={item.decided_at} />
          </p>
          {item.note ? <p className={styles.muted}>{item.note}</p> : null}
        </section>
      ) : decidable ? (
        <section className={styles.decision} aria-labelledby="decision-title">
          <h3 id="decision-title" className={styles.blockTitle}>
            Decide
          </h3>
          {action === "link" ? (
            <>
              <RadioGroup
                label="This name is"
                orientation="horizontal"
                options={[
                  { value: "substance", label: "A known substance" },
                  { value: "material", label: "Another name for a material" },
                ]}
                value={linkMode}
                onValueChange={(value) => setLinkMode(value)}
              />
              {linkMode === "substance" ? (
                <>
                  {item.suggestions.length ? (
                    <RadioGroup
                      label="Suggestions"
                      options={item.suggestions.map((option) => ({
                        value: option.id,
                        label: option.preferred_name,
                        description: [option.cas, `${option.material_count} materials`].filter(Boolean).join(", "),
                      }))}
                      value={substance ? undefined : (suggestion ?? undefined)}
                      onValueChange={(value) => {
                        setSuggestion(value);
                        setSubstance(null);
                      }}
                    />
                  ) : null}
                  <Combobox<SubstanceListItem>
                    label={item.suggestions.length ? "Or search for a substance" : "Substance"}
                    value={substance}
                    onValueChange={setSubstance}
                    loadOptions={(query, signal) => searchSubstances(query, signal)}
                    queryKey={["review", "substance-search"]}
                    minQueryLength={2}
                    getOptionLabel={substanceLabel}
                    getOptionKey={(option) => option.id}
                    renderOption={(option) => <SubstanceOption substance={option} />}
                    placeholder="Name, synonym or CAS number"
                  />
                </>
              ) : (
                <Combobox<MaterialListItem>
                  label="Material"
                  description="The name moves to that material, with its list items and supplier matches; this one is hidden."
                  value={material}
                  onValueChange={setMaterial}
                  loadOptions={(query, signal) => searchMaterials(query, signal)}
                  queryKey={["review", "material-search"]}
                  minQueryLength={2}
                  getOptionLabel={(option) => option.name}
                  getOptionKey={(option) => option.id}
                  renderOption={(option) => <MaterialOption material={option} />}
                  placeholder="Name, synonym, CAS or short code"
                />
              )}
            </>
          ) : null}
          {action === "confirm_substance" ? (
            <Combobox<SubstanceListItem>
              label="Another substance"
              optional
              description="Leave empty to confirm the match. Rejecting unlinks the material and reopens it as unresolved."
              value={substance}
              onValueChange={setSubstance}
              loadOptions={(query, signal) => searchSubstances(query, signal)}
              queryKey={["review", "substance-search"]}
              minQueryLength={2}
              getOptionLabel={substanceLabel}
              getOptionKey={(option) => option.id}
              renderOption={(option) => <SubstanceOption substance={option} />}
              placeholder="Name, synonym or CAS number"
            />
          ) : null}
          {action === "choose_cas" ? (
            <>
              <RadioGroup
                label="The right CAS number"
                options={[
                  ...item.cas_options.map((cas) => ({ value: cas, label: <span className={styles.mono}>{cas}</span> })),
                  { value: "other", label: "Another number" },
                ]}
                value={casChoice ?? undefined}
                onValueChange={setCasChoice}
              />
              {casChoice === "other" ? (
                <TextField
                  label="CAS number"
                  mono
                  value={otherCas}
                  onValueChange={setOtherCas}
                  placeholder="106-22-9"
                />
              ) : null}
            </>
          ) : null}
          {action === "set_cas" ? (
            item.reason_code === "cas_checksum" ? (
              <TextField
                label="Corrected CAS number"
                mono
                value={otherCas}
                onValueChange={setOtherCas}
                placeholder="106-22-9"
              />
            ) : (
              <p className={styles.muted}>
                Accepting records <span className={styles.mono}>{item.cas_options.join(", ")}</span> as CAS numbers of
                this substance.
              </p>
            )
          ) : null}
          {action === "set_odor_path" ? <OdorPathPicker value={termIds} onChange={setTermIds} /> : null}
          {action === null ? (
            <p className={styles.muted}>Accepting records your decision; nothing else changes.</p>
          ) : null}
          <TextArea label="Note" optional rows={2} value={note} onValueChange={setNote} submitOnModEnter={false} />
          {problem ? (
            <InlineAlert tone="danger" title="Not recorded yet">
              {problem}
            </InlineAlert>
          ) : null}
          <div className={styles.decisionButtons}>
            <Button
              variant="primary"
              pending={decide.isPending && decide.variables?.decision === "accepted"}
              pendingLabel="Saving…"
              onClick={() => submit("accepted")}
            >
              {action ? ACCEPT_LABELS[action] : "Accept"}
            </Button>
            <Button
              pending={decide.isPending && decide.variables?.decision === "rejected"}
              onClick={() => submit("rejected")}
            >
              Reject
            </Button>
            {item.status === "open" ? (
              <Button
                variant="ghost"
                pending={decide.isPending && decide.variables?.decision === "deferred"}
                onClick={() => submit("deferred")}
              >
                Defer
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}
    </article>
  );
}
